
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

// Sjekk om Supabase er konfigurert
const isSupabaseConfigured = supabaseUrl && supabaseAnonKey && 
  supabaseUrl !== 'https://dummy.supabase.co' && 
  supabaseAnonKey !== 'dummy-key';

let supabase;

if (!isSupabaseConfigured) {
  console.warn('Supabase environment variables are missing - running in offline mode');
  // Opprett en minimal mock klient som ikke gjør nettverkskall
  supabase = {
    auth: {
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      signInWithPassword: () => Promise.resolve({ data: null, error: { message: 'Supabase ikke konfigurert' } }),
      signUp: () => Promise.resolve({ data: null, error: { message: 'Supabase ikke konfigurert' } }),
      signOut: () => Promise.resolve({ error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } })
    },
    from: () => ({
      select: () => ({ eq: () => ({ single: () => Promise.resolve({ data: null, error: null }) }) }),
      insert: () => Promise.resolve({ data: null, error: null }),
      update: () => Promise.resolve({ data: null, error: null }),
      delete: () => Promise.resolve({ data: null, error: null }),
      upsert: () => Promise.resolve({ data: null, error: null })
    }),
    functions: {
      invoke: () => Promise.resolve({ data: null, error: { message: 'Supabase ikke konfigurert' } })
    }
  };
} else {
  supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      flowType: 'pkce',
      // Deaktiver automatisk refresh for å unngå token-feil
      storage: {
        getItem: (key) => {
          try {
            return localStorage.getItem(key);
          } catch {
            return null;
          }
        },
        setItem: (key, value) => {
          try {
            localStorage.setItem(key, value);
          } catch {
            // Ignorer feil
          }
        },
        removeItem: (key) => {
          try {
            localStorage.removeItem(key);
          } catch {
            // Ignorer feil
          }
        }
      }
    },
    global: {
      headers: {
        'X-Client-Info': 'supabase-js-web'
      }
    },
    db: {
      schema: 'public'
    }
  });
}

export { supabase, isSupabaseConfigured };

// Forbedret global feilhåndtering
const originalConsoleError = console.error;
console.error = (...args) => {
  const message = args[0]?.toString() || '';
  
  // Filtrer ut alle Supabase-relaterte feil når ikke konfigurert
  if (!isSupabaseConfigured && (
    message.includes('supabase') ||
    message.includes('Supabase') ||
    message.includes('AuthRetryableFetchError') ||
    message.includes('Failed to fetch') ||
    message.includes('fetch failed') ||
    message.includes('CORS error') ||
    message.includes('dummy.supabase.co')
  )) {
    return; // Ikke vis disse feilene i det hele tatt
  }
  
  // Filtrer ut refresh token feil
  if (message.includes('Invalid Refresh Token') || 
      message.includes('Refresh Token Not Found') ||
      message.includes('AuthApiError') ||
      message.includes('AuthRetryableFetchError') || 
      message.includes('fetch failed') ||
      message.includes('CORS error') ||
      message.includes('supabase')) {
    console.warn('Supabase connection issue (non-critical):', message);
    return;
  }
  
  originalConsoleError.apply(console, args);
};

// Global error handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  const message = event.reason?.message || event.reason?.toString() || '';
  
  if (message.includes('Invalid Refresh Token') ||
      message.includes('Refresh Token Not Found') ||
      message.includes('AuthApiError') ||
      message.includes('supabase') ||
      message.includes('Failed to fetch')) {
    console.warn('Supabase auth issue handled (non-critical):', message);
    event.preventDefault(); // Forhindre at feilen vises i konsollen
  }
});
